import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetPayStation } from 'src/features/payment/api/useGetPayStation';
import {
    useGetPayStationActiveOperation,
    GET_PAY_STATION_ACTIVE_OPERATION_RESOURCE,
} from 'src/features/payment/api/useGetPayStationActiveOperation';
import DeletePayStationFormModal from 'src/features/payment/components/forms/DeletePayStationFormModal';
import UpdatePayStationFormModal from 'src/features/payment/components/forms/UpdatePayStationFormModal';
import PayStationActionButtons from 'src/features/payment/components/PayStationActionButtons';
import CashLevelsTable from 'src/features/payment/components/tables/CashLevelsTable';
import OperationsTable from 'src/features/payment/components/tables/OperationsTable';
import PayStationEventsTable from 'src/features/payment/components/tables/PayStationEventsTable';
import {
    payStationOperationTypeBadgeOptions,
    payStationPaymentTypeTranslationMap,
    payStationUiModelTranslationMap,
    pipelineProfileTranslationMap,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PAY_STATION_UI_MODAL, PayStationNominal, PayStationPaymentType } from 'src/features/payment/utils/types';
import { languageOptions } from 'src/features/profile/utils';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { PayStationChange, WebSocketEvent } from 'src/features/visitor/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { LOCALE } from 'src/providers/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockTitle,
    SectionBlockSubtitle,
} from 'src/registry/components/blocks/SectionBlock';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';

const formatNominals = (nominals?: PayStationNominal[]) => {
    if (!nominals || nominals.length === 0) return '-';

    return nominals.reduce((acc, nominal) => {
        if (nominal.enable) acc = `${acc}${acc && ', '}${nominal.value} ${nominal.currency}`;

        return acc;
    }, '');
};
export default function PayStationDashboard() {
    const { formatMessage, locale } = useIntl();

    const { payStationId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    const { parkingId } = useParking();
    const { data } = useGetPayStation({ parkingId, payStationId });

    const acceptsCashPayments = data?.settings.enabled_payment_methods.includes(PayStationPaymentType.CASH);

    const { data: activeOperationData } = useGetPayStationActiveOperation({
        payStationId,
        parkingId,
    });

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            const payStationEvent = event as PayStationChange;
            if (payStationId === payStationEvent.data?.pay_station_id) {
                void queryClient.invalidateQueries({
                    queryKey: [GET_PAY_STATION_ACTIVE_OPERATION_RESOURCE],
                });
            }
        }, 500);
        subscribe(WebSocketGroup.PAY_STATION, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.PAY_STATION, handleWebSocket);
    }, [subscribe, unsubscribe, payStationId]);

    React.useEffect(() => {
        setTitle(formatMessage(paymentsMessages.payStationDashboard));

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const managePayStationsPermission = useHasPermissions([PERMISSION.MANAGE_PAY_STATIONS]);
    const accessPayStationEvents = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_EVENTS]);
    const accessPayStationOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);
    const accessPayStation = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);

    const siteName = data?.site?.translations.find((translation) => translation.language === locale)?.name ?? '-';

    const defaultTabValue = (() => {
        if (accessPayStationEvents) return 'events';
        if (accessPayStationOperations) return 'operations';
        return 'cash-levels';
    })();

    const dashboardData = {
        section1: [
            {
                title: paymentsMessages.site,
                value: data?.site?.id ? (
                    <LinkWithIcon
                        to={`/${PATH.ADMINISTRATION}/site/${data.site.id}`}
                        linkTitle={siteName}
                        hasPermissions={accessSites}
                    />
                ) : (
                    siteName
                ),
            },
            {
                title: paymentsMessages.uiModel,
                value: data?.ui_mode ? formatMessage(payStationUiModelTranslationMap[data.ui_mode]) : '-',
            },
            {
                title: paymentsMessages.backendUrl,
                value: data?.backend_url ?? '-',
            },
            {
                title: paymentsMessages.backendApiKey,
                value: data?.backend_api_key ?? '-',
            },
            {
                title: paymentsMessages.activeOperation,
                value: activeOperationData?.operation_type ? (
                    <Badge
                        theme={payStationOperationTypeBadgeOptions[activeOperationData.operation_type]?.theme ?? 'gray'}
                    >
                        {formatMessage(
                            payStationOperationTypeBadgeOptions[activeOperationData.operation_type]?.label ??
                                paymentsMessages.unknownOperation,
                        )}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            ...(data?.ui_mode !== PAY_STATION_UI_MODAL.INTERACTIVE
                ? [
                      {
                          title: gatewayMessages.gateway,
                          value:
                              data?.gateway?.translations?.find((translation) => translation.language === locale)
                                  ?.name ?? '-',
                      },
                  ]
                : []),
        ],
        section2: [
            {
                title: paymentsMessages.pipelineProfile,
                value: data?.settings?.pipeline_profile
                    ? formatMessage(pipelineProfileTranslationMap[data.settings.pipeline_profile])
                    : '-',
            },
            {
                title: paymentsMessages.enablePaymentMethods,
                value: data?.settings?.enabled_payment_methods
                    ?.map((method) => formatMessage(payStationPaymentTypeTranslationMap[method]))
                    .join(', '),
            },

            ...(data?.ui_mode === PAY_STATION_UI_MODAL.INTERACTIVE
                ? [
                      {
                          title: paymentsMessages.servicePassword,
                          value: data?.settings.password,
                      },
                      {
                          title: paymentsMessages.languages,
                          value: data?.settings?.languages
                              ?.map((lang) => formatMessage(languageOptions[lang]))
                              .join(', '),
                      },
                      {
                          title: paymentsMessages.defaultLanguage,
                          value: data?.settings.default_language
                              ? formatMessage(languageOptions[data?.settings.default_language])
                              : '-',
                      },
                      {
                          title: paymentsMessages.lostTicket,
                          value: <BooleanBadge booleanField={data?.settings.lost_ticket} />,
                      },
                      {
                          title: paymentsMessages.enableFreeStay,
                          value: <BooleanBadge booleanField={data?.settings.enable_free_stay} />,
                      },
                      {
                          title: paymentsMessages.enableSubscription,
                          value: <BooleanBadge booleanField={data?.settings.enable_subscriptions} />,
                      },
                      {
                          title: paymentsMessages.enableSettingsEnter,
                          value: <BooleanBadge booleanField={data?.settings.enable_settings_enter} />,
                      },
                      {
                          title: paymentsMessages.webId,
                          value: data?.settings.web_id,
                      },
                      {
                          title: paymentsMessages.homeQR,
                          value: data?.settings.home_qr_value,
                      },
                      {
                          title: paymentsMessages.supportPhone,
                          value: data?.settings.support_phone,
                      },
                      {
                          title: paymentsMessages.homePayTextEN,
                          value: data?.settings?.home_pay_button_text?.[LOCALE.EN],
                      },
                      {
                          title: paymentsMessages.homePayTextBG,
                          value: data?.settings?.home_pay_button_text?.[LOCALE.BG],
                      },
                  ]
                : []),
            {
                title: paymentsMessages.currencies,
                value: data?.settings?.currencies?.join(', '),
            },

            ...(data?.settings?.enabled_payment_methods?.includes(PayStationPaymentType.CASH)
                ? [
                      {
                          title: paymentsMessages.coinAcceptedNominals,
                          value: formatNominals(data?.settings?.coin_selector?.accepted_nominals),
                      },
                      {
                          title: paymentsMessages.coinPayoutNominals,
                          value: formatNominals(data?.settings?.coin_selector?.payout_nominals),
                      },
                      {
                          title: paymentsMessages.banknoteAcceptedNominals,
                          value: formatNominals(data?.settings?.banknote_validator?.accepted_nominals),
                      },
                      {
                          title: paymentsMessages.banknotePayoutNominals,
                          value: formatNominals(data?.settings?.banknote_validator?.payout_nominals),
                      },
                  ]
                : []),
        ],
    };

    return (
        <>
            <BackButton customRoute={`/${PATH.PAYMENTS}/pay-station`} />

            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockHead>
                            <SectionBlockTitle>{data?.name}</SectionBlockTitle>
                            <SectionBlockSubtitle>{data?.code}</SectionBlockSubtitle>
                        </SectionBlockHead>
                        {managePayStationsPermission && data && (
                            <div className="flex flex-row gap-2">
                                <PayStationActionButtons
                                    payStation={data}
                                    acceptsCashPayments={acceptsCashPayments}
                                    disabled={Boolean(activeOperationData?.operation_type)}
                                />
                                <UpdatePayStationFormModal selectedPayStation={data}>
                                    <Button
                                        size="mdIcon"
                                        variant="outline"
                                        disabled={Boolean(activeOperationData?.operation_type)}
                                    >
                                        <div className="block md:hidden">
                                            <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        </div>
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdatePayStationFormModal>
                                <DeletePayStationFormModal selectedPayStation={data}>
                                    <Button
                                        size="mdIcon"
                                        variant="destructiveLight"
                                        disabled={Boolean(activeOperationData?.operation_type)}
                                    >
                                        <div className="block md:hidden">
                                            <FontAwesomeIcon className="block md:hidden" icon={ICON_MAP.DELETE} />
                                        </div>
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeletePayStationFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <GeneralBlock asChild className="flex-1 2xl:flex-2">
                        <DashboardGeneralBlock dashboardData={dashboardData} indexForLastSeparator={1} />
                    </GeneralBlock>
                </SectionBlock>
            </div>

            <Tabs defaultValue={defaultTabValue}>
                <TabsList>
                    {accessPayStationEvents && (
                        <TabsTrigger value="events">
                            <FormattedMessage {...paymentsMessages.eventLogs} />
                        </TabsTrigger>
                    )}
                    {accessPayStationOperations && (
                        <TabsTrigger value="operations">
                            <FormattedMessage {...paymentsMessages.operations} />
                        </TabsTrigger>
                    )}
                    {accessPayStation && (
                        <TabsTrigger value="cash-levels">
                            <FormattedMessage {...paymentsMessages.cashLevels} />
                        </TabsTrigger>
                    )}
                </TabsList>

                {accessPayStationEvents && (
                    <TabsContent value="events">
                        <PayStationEventsTable />
                    </TabsContent>
                )}
                {accessPayStationOperations && (
                    <TabsContent value="operations">
                        <OperationsTable disablePayStationColumn />
                    </TabsContent>
                )}
                {accessPayStation && (
                    <TabsContent value="cash-levels">
                        <CashLevelsTable />
                    </TabsContent>
                )}
            </Tabs>
        </>
    );
}
